import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api';

/**
 * Which sign-in providers this deployment actually has credentials for.
 *
 * ## Why this is a request rather than a build-time constant
 *
 * Because the answer lives with the client secret, and the client secret lives
 * on the server. A `VITE_` variable would mean the bundle and the API could
 * disagree - a button compiled in against an environment that never got the
 * credentials, which is a button that leads to an error page - and the person
 * who finds out is a stranger trying to sign up.
 *
 * ## Cached for the session
 *
 * It cannot change without a deploy, and it is read on both auth pages and the
 * settings page. `staleTime: Infinity` means one request per page load rather
 * than one per screen.
 */
export interface AuthProviders {
  discord: boolean;
}

export function useAuthProviders() {
  return useQuery({
    queryKey: ['auth', 'providers'],
    // `turnstile`: the human check's site key, or null when it is off.
    queryFn: () => api.get<{ providers: AuthProviders; turnstile: string | null }>('/auth/providers', { anonymous: true }),
    staleTime: Infinity,
    /*
     * A failure here must not stop anybody signing in. The hook's callers treat
     * "we do not know" as "no buttons", which leaves the email form working.
     */
    retry: false,
  });
}

/**
 * What the sign-in page should say when it is reached with `?error=`.
 *
 * The callback can only communicate by redirecting, so these are the words for
 * the handful of things it can decide. Deliberately vague about the ordinary
 * failure: somebody who has just been bounced back from Discord does not need a
 * diagnosis, they need to know whether to press it again.
 */
export function providerError(code: string | null): string | null {
  switch (code) {
    case 'discord':
      return 'That did not go through. Try again, or sign in with your email.';
    case 'already-linked':
      return 'That account already has a different Discord connected. Sign in with your email, then change it in settings.';
    case 'unavailable':
      return 'Signing in with Discord is not switched on here.';
    default:
      return null;
  }
}
